import { useCallback, useState } from "react";

import { chakra } from "@chakra-ui/react";
import { Trans, useTranslation } from "next-i18next";
import Router from "next/router";

import Icon from "@components/Icon";
import { useMessage } from "@components/Message";
import useRecaptcha from "@lib/recaptcha/useRecaptcha";
import { useAuth } from "@modules/auth";
import { pushLoginEvent, pushSignUpEvent } from "@modules/gtm";
import { ROUTES } from "@utils/constants";
import { reportMessage } from "@utils/errorReporting";

import redirectContractedUser from "../services/redirectContractedUser";
import sendVerificationEmail from "../services/sendVerificationEmail";
import submitLogin from "../services/submitLogin";
import { LoginFields, TLoginEmailFormData, TLoginFacebookFormData, TLoginFieldErrors,
  TLoginGoogleFormData, TUseLoginReturn } from "../types";

const useLogin = (): TUseLoginReturn => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<TLoginFieldErrors>();
  const { setAuthData } = useAuth();
  const setMessage = useMessage();
  const handleRecaptcha = useRecaptcha("login");

  const handleSuccess = useCallback(() => {
    setMessage({
      message: t("resend_verification_email_success"),
      props: {
        variant: "success",
        icon: <Icon icon="success" size="1.45rem" />,
        canClose: true,
        padding: "0.45rem 0.75rem",
      },
      zone: "login",
    });
  }, [t, setMessage]);

  const handleError = useCallback(
    (message?: string) => {
      setMessage({
        message: message || t("login-general_error"),
        props: {
          variant: "error",
          icon: <Icon icon="warning" size="1.45rem" />,
          canClose: true,
          padding: "0.45rem 0.75rem",
        },
        zone: "login",
      });
    },
    [t, setMessage]
  );

  const handleVerificationEmail = useCallback(
    async (email: string) => {
      setMessage({ message: "", props: {}, zone: "login" }); // Clearing previous message
      setIsLoading(true);
      const result = await sendVerificationEmail(email);
      setIsLoading(false);
      if (result.isSuccess) {
        handleSuccess();
      } else {
        handleError(result.errorMessage);
      }
    },
    [setMessage, handleSuccess, handleError]
  );

  const handleNotVerified = useCallback(
    (data: LoginFields.EMAIL) => {
      setMessage({
        message: (
          <Trans
            i18nKey="verify_account_forgot_password"
            components={{
              sendlink: (
                <chakra.a
                  href="#"
                  onClick={() => handleVerificationEmail(data)}
                  color="red.lead"
                  sx={{ _hover: { textDecoration: "underline" } }}
                />
              ),
            }}
          />
        ),
        props: {
          variant: "error",
          icon: <Icon icon="warning" size="1.45rem" />,
          canClose: true,
          padding: "0.45rem 0.75rem",
        },
        zone: "login",
      });
    },
    [setMessage, handleVerificationEmail]
  );

  const handleLogin = useCallback(
    async (isFacebook: boolean, isGoogle: boolean, data: TLoginEmailFormData | TLoginFacebookFormData | TLoginGoogleFormData, recaptchaToken: string) => {
      setMessage({ message: "", props: {}, zone: "login" }); // Clearing previous message
      const result = await submitLogin(isFacebook, isGoogle, data, recaptchaToken);

      if (result.notVerifiedFlag && !isFacebook && !isGoogle) {
        handleNotVerified(data[LoginFields.EMAIL]);
        setIsLoading(false);
        return result;
      }
      if (!result.isSuccess || (!result.authData && !result.contractedUser)) {
        handleError(result.errorMessage);
        setIsLoading(false);
        return result;
      }

      if (result.contractedUser) {
        redirectContractedUser(result.contractedUser);
        return result;
      }

      if (!result.authData) {
        handleError(result.errorMessage);
        setIsLoading(false);
        return result;
      }

      setAuthData(result.authData);

      if (isGoogle) {
        if (result.isNewUser) {
          pushSignUpEvent("google", result.authData.email, result.authData.phone);
        } else {
          pushLoginEvent("google", result.authData.email, result.authData.phone);
        }
      } else if (isFacebook) {
        if (result.isNewUser) {
          pushSignUpEvent("facebook", result.authData.email, result.authData.phone);
        } else {
          pushLoginEvent("facebook", result.authData.email, result.authData.phone);
        }
      } else {
        pushLoginEvent("email", result.authData.email, result.authData.phone);
      }

      if (result.authData.hasInvalidDefaultAddresses) {
        Router.push({ pathname: ROUTES.PROFILE_MY_ADDRESSES });
        return result;
      }

      if (result.authData.isPasswordExpired) {
        Router.push({ pathname: ROUTES.PROFILE_ACCOUNT });
      } else {
        Router.push({ pathname: ROUTES.LANDING });
      }

      return result;
    },

    [setMessage, setAuthData, handleNotVerified, handleError]
  );

  const handleRecaptchaAndLogin = useCallback(
    async (data: TLoginFacebookFormData | TLoginEmailFormData | TLoginGoogleFormData, isFacebook: boolean, isGoogle: boolean) => {
      setIsLoading(true);
      const recaptchaToken = await handleRecaptcha();

      if (!recaptchaToken) {
        reportMessage("Recaptcha token not available");
        handleError();
        setIsLoading(false);
        return;
      }
      const result = await handleLogin(isFacebook, isGoogle, data, recaptchaToken);

      if (!result.isSuccess && !isFacebook && !isGoogle) {
        setFieldErrors(result.fieldErrors || undefined);
      }
    },
    [handleLogin, handleError, handleRecaptcha]
  );

  const handleEmailLogin = (data: TLoginEmailFormData) => handleRecaptchaAndLogin(data, false, false);
  const handleFacebookLogin = (data: TLoginFacebookFormData) => handleRecaptchaAndLogin(data, true, false);
  const handleGoogleLogin = (data: TLoginGoogleFormData) => handleRecaptchaAndLogin(data, false, true);

  return { isLoading, fieldErrors, handleEmailLogin, handleFacebookLogin, handleGoogleLogin };
};

export default useLogin;
